<template>
  <div class="claim-detail">
    <div v-if="claimIsLoading">
      <loading-spinner class="claim-detail__loading-spinner" />
    </div>
    <div v-else>
      <div v-if="notFound">
        <claim-not-found :claim-number="claimNumber" />
      </div>
      <div v-else-if="claimIsLoaded" id="ClaimDetails" class="container">
        <div class="no-gutters">
          <div class="row no-gutters">
            <b-col md="8" class="">
              <h1>Claim #{{ claimNumber }}</h1>
            </b-col>
            <b-col md="4" class="text-right">
              <strong>Status: {{ claimDetails.ClaimStatus }}</strong>
            </b-col>
          </div>
        </div>
        <b-col id="claimInformationHeader" lg="12" class="my-3 claimsDetails-SubHeaders"> Claim Information </b-col>
        <b-col id="claimInformationDetail" lg="12" class="p-0">
          <div class="row no-gutters from-group">
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Dealer Claim Reference:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimDetails.CustomerReferenceNumber }}
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Invoice Number:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              <a :href="invoiceLink">{{ claimDetails.ClaimInvoiceNumber }}</a>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Invoice Credit Number:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              <router-link
                v-if="this.IsNotNullOrUndefined(claimDetails, 'Credit.InvoiceNumber')"
                :to="creditInvoiceLink"
                >{{ claimDetails.Credit.InvoiceNumber }}</router-link
              >
            </b-col>
          </div>
          <br />
          <b-row class="no-gutters from-group mb-5">
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Order Number:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              <order-details-link
                :order-number="claimDetails.OrderNumber"
                :order-source="claimDetails.Source"
                :dealer-code="claimDetails.DealerCode"
                :sales-organization="claimDetails.SalesOrganization"
              />
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Authorization Number:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimNumber }}
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Date Submitted:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              <date-time :date-string="claimDetails.DateSubmitted" />
            </b-col>
          </b-row>
        </b-col>
        <b-col id="contactInformationHeader" lg="12" class="my-3 claimsDetails-SubHeaders"> Contact Information </b-col>
        <b-col id="contactInformationDetail" lg="12" class="p-0">
          <b-row class="no-gutters form-group">
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Contact Name:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimDetails.ContactName }}
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Contact Email:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimDetails.ContactEmail }}
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Contact Number:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimDetails.ContactPhone }}
            </b-col>
          </b-row>
          <b-row class="no-gutters from-group mb-5">
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Contact Extension:</strong>
            </b-col>
            <b-col lg="2" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimDetails.ContactPhoneExtension }}
            </b-col>
            <b-col lg="2" md="6" sm="12" class="control-label">
              <strong>Contact Fax:</strong>
            </b-col>
            <b-col lg="4" md="6" sm="12" class="form-control-static claimsDetails-detailData">
              {{ claimDetails.ContactFax }}
            </b-col>
          </b-row>
        </b-col>
        <div v-if="!isFreightClaim">
          <b-col id="partInformationHeader" lg="12" class="my-3 claimsDetails-SubHeaders"> Part Information </b-col>
          <DnTable
            hover
            striped
            :items="claimDetails.Items"
            :fields="partInformationColumns"
            stacked="md"
            :no-provider-filtering="true"
            show-empty
            :no-local-sorting="true"
            class="blue"
          >
            <template #claimReason="{ row }">
              {{ row.item.ClaimReason }}
              <div v-if="row.item.Comments != null"><strong>Comments:</strong> {{ row.item.Comments }}</div>
            </template>
            <template #partNumber="{ row }">
              <router-link :to="{ name: 'PartDetails', query: { oemPartCode: row.item.OrderedOemPartCode } }">
                {{ row.item.OrderedOemPartCode }}
              </router-link>
            </template>
            <template #price="{ row }">
              <currency-text :amount="row.item.OrderedPrice" :currency-code="claimDetails.CurrencyCode" />
            </template>
            <template #extPrice="{ row }">
              <currency-text :amount="row.item.ExtendedPrice" :currency-code="claimDetails.CurrencyCode" />
            </template>
          </DnTable>
        </div>
        <div class="claimsAmounts border border-dark">
          <div class="row mx-0 border-bottom py-1">
            <b-col lg="10" md="6" sm="12" class="control-label my-1">
              Estimated Claim Amount:
              <!-- TODO Translation -->
            </b-col>
            <b-col lg="2" md="6" sm="12" class="claimsDetails-detailData text-right my-1">
              <currency-text :amount="claimDetails.EstimatedClaimAmount" :currency-code="claimDetails.CurrencyCode" />
            </b-col>
          </div>
          <div class="row mx-0 border-bottom py-1">
            <b-col lg="10" md="6" sm="12" class="control-label my-1">
              Claim For Freight:
              <!-- TODO Translation -->
            </b-col>
            <b-col lg="2" md="6" sm="12" class="claimsDetails-detailData text-right my-1">
              <currency-text :amount="claimDetails.ClaimForFreight" :currency-code="claimDetails.CurrencyCode" />
            </b-col>
          </div>
          <div class="row modify mx-0 modify-claimsAmounts">
            <b-col lg="10" md="6" sm="12" class="control-label">
              ESTIMATED CLAIM TOTAL:
              <!-- TODO Translation -->
            </b-col>
            <b-col lg="2" md="6" sm="12" class="claimsDetails-detailData text-right">
              <currency-text :amount="claimTotalWithFreight" :currency-code="claimDetails.CurrencyCode" />
            </b-col>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ErrorLogService, ClaimsService } from "@/services";
import ClaimNotFound from "./ClaimNotFound";
import DateTime from "@/components/Elements/DateTime";
import { CurrencyText, LoadingSpinner, OrderDetailsLink } from "@/components";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  components: {
    CurrencyText,
    DateTime,
    OrderDetailsLink,
    ClaimNotFound,
    LoadingSpinner,
    DnTable,
  },
  props: {
    claimNumber: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    claimDetails: {
      Credit: {
        InvoiceNumber: Number,
      },
    },
    partInformationColumns: [
      { key: "partNumber", label: "Part Number", sortable: false },
      { key: "PartDescription", label: "Part Description", sortable: false },
      { key: "claimReason", label: "Claim Reason", sortable: false },
      { key: "ReturnQuantity", label: "Quantity", sortable: false },
      { key: "price", label: "Price", sortable: false },
      { key: "extPrice", label: "Ext. Price", sortable: false },
    ],
    claimIsLoading: true,
    claimIsLoaded: false,
    notFound: false,
  }),
  computed: {
    ...mapGetters(["user", "activeAccountId"]),
    claimTotalWithFreight() {
      return this.claimDetails.EstimatedClaimAmount + this.claimDetails.ClaimForFreight;
    },
    invoiceLink() {
      return (
        "/orders/invoices/detail?invoiceId=" +
        this.claimDetails.ClaimInvoiceNumber +
        "&invoiceSource=" +
        this.claimDetails.ClaimInvoiceSource
      );
    },
    creditInvoiceLink() {
      if (this.IsNullOrUndefined(this, "claimDetails.Credit.Id")) {
        return "";
      }
      return (
        "/orders/credits/detail?id=" + this.claimDetails.Credit.Id + "&source=" + this.claimDetails.Credit.InvoiceSource
      );
    },
    isFreightClaim() {
      if (this.claimNumber?.includes("FR-")) {
        return true;
      }
      return false;
    },
  },
  mounted() {
    this.claimIsLoading = true;
    ClaimsService.GetClaimDetails(this.activeAccountId, this.user.UserId, this.claimNumber)
      .then((claimDetail) => {
        this.claimDetails = claimDetail.data;
      })
      .catch((err) => {
        ErrorLogService.LogException(err);
      })
      .finally(() => {
        this.claimIsLoading = false;
        if (!this.claimDetails || !this.claimDetails.Items) {
          this.notFound = true;
        } else {
          this.claimIsLoaded = true;
        }
      });
  },
};
</script>

<style>
.claim-detail__loading-spinner {
  margin-left: auto;
  margin-right: auto;
}
</style>
